import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ChatCircle, Clock, Barbell, Trophy } from "@phosphor-icons/react";
import api from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function WorkoutCard({ workout, onChange }) {
  const [liked, setLiked] = useState(workout.liked_by_me);
  const [likes, setLikes] = useState(workout.likes_count);
  const [pending, setPending] = useState(false);

  const toggleLike = async () => {
    if (pending) return;
    setPending(true);
    try {
      if (liked) {
        await api.delete(`/workouts/${workout.id}/like`);
        setLiked(false);
        setLikes((l) => Math.max(0, l - 1));
      } else {
        await api.post(`/workouts/${workout.id}/like`);
        setLiked(true);
        setLikes((l) => l + 1);
      }
      onChange && onChange();
    } finally {
      setPending(false);
    }
  };

  const totalSets = workout.exercises?.reduce((sum, ex) => sum + (ex.sets?.length || 0), 0) || 0;

  return (
    <article
      data-testid="workout-feed-item"
      className="border border-[#27272A] bg-[#121212] card-hover fade-in"
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-[#27272A]">
        <Link
          to={workout.user ? `/u/${workout.user.id}` : "#"}
          className="w-10 h-10 bg-[#27272A] flex items-center justify-center overflow-hidden"
          data-testid="workout-user-avatar"
        >
          {workout.user?.avatar_url ? (
            <img src={workout.user.avatar_url} alt={workout.user.name} className="w-full h-full object-cover" />
          ) : (
            <span className="font-display font-bold text-white text-lg">
              {(workout.user?.name || "?").charAt(0).toUpperCase()}
            </span>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <Link
            to={workout.user ? `/u/${workout.user.id}` : "#"}
            className="font-display font-bold text-white hover:text-[#FF3B30] transition-colors uppercase tracking-wide"
          >
            {workout.user?.name || "Unknown"}
          </Link>
          <div className="text-xs text-zinc-500 font-mono">{formatDate(workout.created_at)}</div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white mb-1">
          {workout.title}
        </h3>
        {workout.description && (
          <p className="text-zinc-400 text-sm mb-4">{workout.description}</p>
        )}

        {/* Stats bar */}
        <div className="grid grid-cols-3 border border-[#27272A] mb-4">
          <div className="p-3 border-r border-[#27272A]">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Volume</div>
            <div className="font-display text-2xl font-black text-white">
              {Math.round(workout.volume).toLocaleString()}
              <span className="text-xs text-zinc-500 font-mono ml-1">kg</span>
            </div>
          </div>
          <div className="p-3 border-r border-[#27272A]">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Sets</div>
            <div className="font-display text-2xl font-black text-white">{totalSets}</div>
          </div>
          <div className="p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Time</div>
            <div className="font-display text-2xl font-black text-white">
              {workout.duration_min}
              <span className="text-xs text-zinc-500 font-mono ml-1">min</span>
            </div>
          </div>
        </div>

        {/* Exercise list */}
        <div className="space-y-1 mb-4">
          {workout.exercises?.slice(0, 4).map((ex, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-[#1E1E1E] last:border-b-0">
              <div className="flex items-center gap-2">
                <Barbell size={14} weight="bold" className="text-[#FF3B30]" />
                <span className="text-sm font-semibold text-white">{ex.name}</span>
              </div>
              <span className="text-xs text-zinc-500 font-mono">
                {ex.sets?.length} × {ex.sets?.[0]?.reps || 0} @ {ex.sets?.[0]?.weight || 0}kg
              </span>
            </div>
          ))}
          {workout.exercises?.length > 4 && (
            <div className="text-xs text-zinc-500 italic pt-1">+{workout.exercises.length - 4} more</div>
          )}
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex items-center gap-4 px-4 py-3 border-t border-[#27272A]">
        <button
          data-testid="workout-like-button"
          onClick={toggleLike}
          disabled={pending}
          className={`flex items-center gap-2 transition-colors ${liked ? "text-[#FF3B30]" : "text-zinc-400 hover:text-white"}`}
        >
          <Heart size={20} weight={liked ? "fill" : "bold"} />
          <span className="text-sm font-mono">{likes}</span>
        </button>
        <div className="flex items-center gap-2 text-zinc-400">
          <ChatCircle size={20} weight="bold" />
          <span className="text-sm font-mono">{workout.comments_count}</span>
        </div>
      </div>
    </article>
  );
}
